import { useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBeforeUnload, useBlocker, useNavigate, useParams } from 'react-router-dom';

import { Button } from '../../components/ui/Button.js';
import { ConfirmModal } from '../../components/ui/ConfirmModal.js';
import { Input } from '../../components/ui/Input.js';
import { ToastStack } from '../../components/ui/Toast.js';
import { useApi } from '../../hooks/useApi.js';
import { useToasts } from '../../hooks/useToasts.js';
import { ApiError } from '../../lib/api.js';
import type { TipoAusencia, TipoAusenciaCategoria } from './types.js';

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

// Prompt AD — Pantalla 2: mismo componente para ver/editar un tipo existente
// (:id) y para crear uno nuevo (/tipos-ausencia/nuevo), igual patrón que
// Días Festivos (AC-2). Los campos viven en estado local (borrador) y solo
// se envían al backend al presionar "Guardar" — mismo mecanismo de
// useBlocker/useBeforeUnload + ConfirmModal ya implementado en AC-3 para el
// guard de salida sin guardar.
export function TipoAusenciaDetalle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get, post, patch } = useApi();
  const queryClient = useQueryClient();
  const { toasts, showToast, dismiss } = useToasts();

  const esNuevo = !id;

  const { data: tipo, isLoading } = useQuery({
    queryKey: ['tipos-ausencia', id],
    queryFn: () => get<TipoAusencia>(`/tipos-ausencia/${id}`),
    enabled: !esNuevo,
  });

  const [nombre, setNombre] = useState('');
  const [categoria, setCategoria] = useState<TipoAusenciaCategoria>('antiguedad');
  // Prompt BB-1(RH) — categoriaVisible: fija a "Vacaciones" (no editable)
  // para 'antiguedad'; editable con default = nombre para cupon_fijo/
  // permisos_especiales. `categoriaVisibleManual` distingue "todavía sigue
  // el default de nombre" (sigue sincronizándose en vivo mientras se
  // escribe el nombre) de "el usuario ya lo tocó a mano" (deja de
  // sincronizarse) — mismo criterio que el servicio: un rename posterior no
  // debe pisar un valor ya personalizado.
  const [categoriaVisible, setCategoriaVisible] = useState('');
  const [categoriaVisibleManual, setCategoriaVisibleManual] = useState(false);
  const [anioDesde, setAnioDesde] = useState('');
  const [anioHasta, setAnioHasta] = useState('');
  const [diasOtorgados, setDiasOtorgados] = useState('');
  const [diasOtorgadosNomina, setDiasOtorgadosNomina] = useState('');
  const [cargado, setCargado] = useState(false);

  // Bug real (Prompt AD-2): el guard de salida usaba una comparación
  // DERIVADA (campos locales vs. el `tipo` cacheado por useQuery) en vez de
  // un flag explícito. Tras un Guardar exitoso, `invalidateQueries` dispara
  // un refetch ASÍNCRONO — hasta que ese refetch resuelve, `tipo` sigue
  // siendo el valor viejo, así que la comparación seguía dando "sucio" en
  // el mismo tick en que se llamaba a navigate() (modo alta), disparando el
  // modal de "¿Salir sin guardar?" sobre un guardado que ya había tenido
  // éxito. Fix: un flag explícito `isDirty`, marcado a mano en cada cambio
  // de campo y limpiado (con flushSync, ver más abajo) ANTES de cualquier
  // navigate() posterior al guardado.
  const [isDirty, setIsDirty] = useState(false);

  // Precarga del formulario en modo edición — no marca dirty (mismo criterio
  // que NuevoCliente.tsx): cargado=true recién después de setear los valores
  // originales, y esta precarga nunca toca isDirty.
  useEffect(() => {
    if (esNuevo || !tipo || cargado) return;
    setNombre(tipo.nombre);
    setCategoria(tipo.categoria);
    setCategoriaVisible(tipo.categoria_visible);
    // Ya es un valor real guardado — se trata como "manual" para que un
    // rename posterior no lo pise en silencio (mismo criterio que el servicio).
    setCategoriaVisibleManual(true);
    setAnioDesde(tipo.anio_desde !== null ? String(tipo.anio_desde) : '');
    setAnioHasta(tipo.anio_hasta !== null ? String(tipo.anio_hasta) : '');
    setDiasOtorgados(String(tipo.dias_otorgados));
    setDiasOtorgadosNomina(tipo.dias_otorgados_nomina !== null ? String(tipo.dias_otorgados_nomina) : '');
    setCargado(true);
  }, [esNuevo, tipo, cargado]);

  function actualizarNombre(v: string) {
    setNombre(v);
    // Sigue el default = nombre mientras el usuario no haya tocado
    // categoriaVisible a mano (y no aplica a 'antiguedad', que siempre es
    // "Vacaciones" fijo).
    if (categoria !== 'antiguedad' && !categoriaVisibleManual) setCategoriaVisible(v);
    setIsDirty(true);
  }

  function actualizarCategoriaVisible(v: string) {
    setCategoriaVisible(v);
    setCategoriaVisibleManual(true);
    setIsDirty(true);
  }

  function handleCategoriaChange(nueva: TipoAusenciaCategoria) {
    setCategoria(nueva);
    if (nueva !== 'antiguedad') {
      setAnioDesde('');
      setAnioHasta('');
      // Al pasar DE antiguedad a otra categoría, reinicia al default (nombre)
      // — no tiene sentido conservar "Vacaciones" para un cupón fijo nuevo.
      if (!categoriaVisibleManual || categoriaVisible === 'Vacaciones') setCategoriaVisible(nombre);
    }
    setIsDirty(true);
  }

  function actualizarAnioDesde(v: string) {
    setAnioDesde(v);
    setIsDirty(true);
  }

  function actualizarAnioHasta(v: string) {
    setAnioHasta(v);
    setIsDirty(true);
  }

  function actualizarDiasOtorgados(v: string) {
    setDiasOtorgados(v);
    setIsDirty(true);
  }

  function actualizarDiasOtorgadosNomina(v: string) {
    setDiasOtorgadosNomina(v);
    setIsDirty(true);
  }

  const blocker = useBlocker(isDirty);

  useBeforeUnload(
    useCallback(
      (e: BeforeUnloadEvent) => {
        if (!isDirty) return;
        e.preventDefault();
        e.returnValue = '';
      },
      [isDirty],
    ),
  );

  const guardarMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        nombre,
        categoria,
        // 'antiguedad' siempre se fuerza a "Vacaciones" en el servicio sin
        // importar lo que se mande — se omite aquí para no sugerir que es
        // editable.
        categoria_visible: categoria === 'antiguedad' ? undefined : categoriaVisible,
        anio_desde: categoria === 'antiguedad' && anioDesde !== '' ? Number(anioDesde) : null,
        anio_hasta: categoria === 'antiguedad' && anioHasta !== '' ? Number(anioHasta) : null,
        dias_otorgados: Number(diasOtorgados),
        dias_otorgados_nomina: categoria === 'antiguedad' && diasOtorgadosNomina !== '' ? Number(diasOtorgadosNomina) : null,
      };
      return esNuevo ? post<TipoAusencia>('/tipos-ausencia', payload) : patch<TipoAusencia>(`/tipos-ausencia/${id}`, payload);
    },
    onSuccess: resultado => {
      // flushSync fuerza a React a aplicar y confirmar (render + efectos)
      // isDirty=false de forma SÍNCRONA antes de seguir — sin esto, el
      // navigate() de abajo podía dispararse mientras el router todavía veía
      // el valor viejo de `isDirty` (true) y bloqueaba la navegación con el
      // modal de "cambios sin guardar" sobre un guardado que ya tuvo éxito.
      flushSync(() => setIsDirty(false));
      queryClient.invalidateQueries({ queryKey: ['tipos-ausencia'] });
      if (esNuevo) {
        navigate(`/tipos-ausencia/${resultado.id}`, { replace: true });
        // Prompt AF-2(RH) — antes solo navegaba en silencio; el guardado
        // exitoso al crear no tenía ninguna confirmación visual.
        showToast('success', 'Tipo de ausencia creado correctamente');
      } else {
        queryClient.invalidateQueries({ queryKey: ['tipos-ausencia', id] });
        showToast('success', 'Cambios guardados correctamente');
      }
    },
    onError: err => showToast('error', errorMessage(err, 'No fue posible guardar')),
  });

  function handleGuardar() {
    if (!nombre.trim()) {
      showToast('error', 'El nombre es obligatorio');
      return;
    }
    if (categoria === 'antiguedad' && anioDesde === '') {
      showToast('error', 'Año desde es obligatorio para categoría Antigüedad');
      return;
    }
    if (diasOtorgados === '' || Number(diasOtorgados) < 0) {
      showToast('error', 'Días otorgados debe ser un número válido');
      return;
    }
    if (diasOtorgadosNomina !== '' && Number(diasOtorgadosNomina) < 0) {
      showToast('error', 'Días otorgados nómina debe ser un número válido');
      return;
    }
    guardarMutation.mutate();
  }

  function handleCancelar() {
    if (esNuevo) {
      navigate('/tipos-ausencia');
      return;
    }
    if (!tipo) return;
    setNombre(tipo.nombre);
    setCategoria(tipo.categoria);
    setCategoriaVisible(tipo.categoria_visible);
    setCategoriaVisibleManual(true);
    setAnioDesde(tipo.anio_desde !== null ? String(tipo.anio_desde) : '');
    setAnioHasta(tipo.anio_hasta !== null ? String(tipo.anio_hasta) : '');
    setDiasOtorgados(String(tipo.dias_otorgados));
    setDiasOtorgadosNomina(tipo.dias_otorgados_nomina !== null ? String(tipo.dias_otorgados_nomina) : '');
    setIsDirty(false);
  }

  const confirmSalida = (
    <ConfirmModal
      open={blocker.state === 'blocked'}
      title="¿Salir sin guardar?"
      body="Tienes cambios sin guardar. Si sales ahora, se perderán."
      confirmLabel="Salir sin guardar"
      danger
      onConfirm={() => blocker.state === 'blocked' && blocker.proceed()}
      onCancel={() => blocker.state === 'blocked' && blocker.reset()}
    />
  );

  if (!esNuevo && (isLoading || !tipo)) {
    return <div>Cargando…</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--heading)' }}>
          {esNuevo ? 'Nuevo Tipo de Ausencia' : tipo!.nombre}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={handleCancelar} disabled={!esNuevo && (!isDirty || guardarMutation.isPending)}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={!isDirty || guardarMutation.isPending}>
            {guardarMutation.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </div>

      <div className="plat-card" style={{ maxWidth: 520 }}>
        <Input label="Nombre" value={nombre} onChange={e => actualizarNombre(e.target.value)} placeholder="Aniversario 1" />

        <div className="field">
          <label>Categoría</label>
          <select
            value={categoria}
            onChange={e => handleCategoriaChange(e.target.value as TipoAusenciaCategoria)}
            style={{ padding: '7px 10px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13.5, width: '100%' }}
          >
            <option value="antiguedad">Antigüedad</option>
            <option value="cupon_fijo">Cupón fijo</option>
            <option value="permisos_especiales">Permisos especiales</option>
          </select>
        </div>

        {categoria === 'antiguedad' ? (
          <Input label="Categoría visible" value="Vacaciones" disabled />
        ) : (
          <Input label="Categoría visible" value={categoriaVisible} onChange={e => actualizarCategoriaVisible(e.target.value)} />
        )}

        {categoria === 'antiguedad' && (
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Input label="Año desde" type="number" value={anioDesde} onChange={e => actualizarAnioDesde(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <Input label="Año hasta (opcional)" type="number" value={anioHasta} onChange={e => actualizarAnioHasta(e.target.value)} />
            </div>
          </div>
        )}

        <Input label="Días otorgados" type="number" value={diasOtorgados} onChange={e => actualizarDiasOtorgados(e.target.value)} />
        {categoria === 'antiguedad' && (
          <>
            <Input
              label="Días otorgados nómina (opcional)"
              type="number"
              value={diasOtorgadosNomina}
              onChange={e => actualizarDiasOtorgadosNomina(e.target.value)}
            />
            <div className="ff-hint" style={{ marginTop: -8 }}>
              Para el saldo de nómina (antigüedad desde la fecha de ingreso nómina). Vacío = usa Días otorgados.
            </div>
          </>
        )}
        {categoria === 'permisos_especiales' && (
          <div className="ff-hint" style={{ marginTop: -8 }}>
            0 = indefinido, se evalúa caso por caso
          </div>
        )}
      </div>

      <ToastStack toasts={toasts} onDismiss={dismiss} />

      {confirmSalida}
    </div>
  );
}
